import { OperationType } from '@aavaz-ai/eql-parser-ts/dist/types';
import { isSavedAnalysis } from '@modules/analysis/helpers';
import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { getChartLabelIdOrName } from '@modules/charts/utils';
import { AggregateFormulaResultTable } from '@modules/quantify/components/AggregateFormulaResultTable';
import { useIsSingleRecordQuery } from '@modules/quantify/hooks';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { useFetchEQLReferences } from '@modules/queryBuilder/hooks';
import { isEqual } from 'lodash';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { reasonWhereClauseGenerator } from 'v2/core/services/queryBuilder/whereClauseGenerator';
import { QuantifyReportTable } from './QuantifyReportTable';

export function QuantifyReportTableWidget() {
  const isSingleRecordQuery = useIsSingleRecordQuery();

  const analysisID = useAnalysisStore(({ id }) => id);

  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);
  const compareTo = useQuantifyStore(({ query }) => query.compareQuery);
  const isTableQueryInProgress = useQuantifyStore(
    ({ visual }) => visual.isTableQueryInProgress,
  );
  const isReasonList = useQuantifyStore(
    ({ query }) => query.groupByField?.field === 'reason',
  );
  const visibleDataPoints = useQuantifyStore(
    ({ query }) => query.visibleDataPoints,
  );
  const tableRows = useQuantifyStore(({ reportData }) =>
    compareTo ? reportData.compare.tableRows : reportData.quantify.tableRows,
  );

  useFetchEQLReferences({
    fetchPolicy: 'cache-and-network',
    skip:
      !isReasonList ||
      !visibleDataPoints?.length ||
      !isSavedAnalysis(analysisID),
    whereClause: isReasonList
      ? reasonWhereClauseGenerator(
          (visibleDataPoints ?? []).map((id) => getChartLabelIdOrName(id)),
          OperationType.OR,
          false,
        )
      : EmptyWhereClause,
    onCompleted: (eqlFieldByID) => {
      let updatedVisibleDataPoints = visibleDataPoints?.map(
        (visibleDataPoint) => {
          const reasonID = getChartLabelIdOrName(visibleDataPoint);
          const mappedReasonID =
            eqlFieldByID['reason']?.eqlFieldValueByID?.[reasonID]?.value;

          return mappedReasonID
            ? visibleDataPoint.replaceAll(reasonID, mappedReasonID)
            : visibleDataPoint;
        },
      );

      updatedVisibleDataPoints = [...new Set(updatedVisibleDataPoints)];

      if (!isEqual(updatedVisibleDataPoints, visibleDataPoints)) {
        dispatch({
          type: QuantifyActionType.SET_QUERY,
          payload: { visibleDataPoints: updatedVisibleDataPoints },
        });
      }
    },
  });

  if (isSingleRecordQuery) {
    return <AggregateFormulaResultTable />;
  }

  if (tableRows === null && !isTableQueryInProgress) {
    return null;
  }

  return <QuantifyReportTable />;
}
